// ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════
//  t603 · B-2443 · EL PORTAL DEL PACIENTE DE UNA CLÍNICA CON LA EDICIÓN LOCAL, POR EL PUENTE DE BIANGO  (31-oct-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════
//
//  Una clínica con Biango instalado en su ordenador (la edición local) y el módulo «portal» encendido manda a sus pacientes enlaces
//  como https://biango.app/l/<instalación>/portal o https://biango.app/l/<instalación>/encuesta/<token>. La nube sirve ESTAS mismas
//  pantallas; lo que piden al servidor no va a la nube sino a la clínica, por el puente:
//     /api/portal/mi   →   /api/puente-local/portal/<instalación>/api/portal/mi
//  (backend/routes/puenteLocal.js, que solo deja pasar las rutas del paciente: services/puenteLocal · RUTAS_PORTAL).
//
//  `instalar()` (main.jsx, antes de pintar nada) lo prepara todo si la dirección empieza por /l/<instalación>, y NO HACE NADA en
//  cualquier otra (la nube y la clínica, como siempre):
//   · el router trabaja debajo de /l/<instalación> (main.jsx le pone ese `basename`);
//   · TODA petición a /api/... de la página (XMLHttpRequest, que es lo que usa axios, fetch y sendBeacon) va por el puente de ESA
//     instalación: así ni un cliente de la app se escapa a la nube por olvido;
//   · LO GUARDADO EN EL NAVEGADOR va aparte por instalación (localStorage y sessionStorage, con el prefijo «l:<instalación>:»): la
//     nube y todas las instalaciones comparten el mismo dominio, y la sesión del portal de una clínica no puede llegarle a otra (ni la
//     de la nube a una clínica);
//   · /l/<instalación> a secas abre el portal, y /l/<instalación>/api/... (un enlace de la Nube compartido) va a su sitio en el puente.
//  Lo mide backend/services/edicion-local-puente-fase2.test.js (§portal) cargando este fichero de verdad.

const RE = /^\/l\/([A-Za-z0-9-]{8,64})(?=\/|$)/

// { instalacion, base, api } si la dirección es la de una instalación por el puente; null si no.
export function detectar(pathname) {
  const m = RE.exec(String(pathname || ''))
  if (!m) return null
  return { instalacion: m[1], base: '/l/' + m[1], api: '/api/puente-local/portal/' + m[1] }
}
// La ruta de la pantalla sin /l/<instalación> delante ('/' si no queda nada).
export function sinPrefijo(pathname, p) {
  const s = String(pathname || '/')
  if (!p || !s.startsWith(p.base)) return s
  return s.slice(p.base.length) || '/'
}
// Una petición a /api/... (relativa o de este mismo origen) por el puente; lo demás, tal cual.
export function rutaApi(url, p, origen = '') {
  if (!p || typeof url !== 'string') return url
  let u = url
  if (origen && u.startsWith(origen + '/')) u = u.slice(origen.length)
  if (!u.startsWith('/api/') || u.startsWith('/api/puente-local/')) return url
  return p.api + u
}
// Una pantalla de la app, dentro de su instalación (para window.location).
export function rutaPantalla(ruta, p) {
  return p && typeof ruta === 'string' && ruta.startsWith('/') && !ruta.startsWith(p.base) ? p.base + ruta : ruta
}
export const prefijoAlmacen = (p) => (p ? 'l:' + p.instalacion + ':' : '')

// LA VUELTA A LA ENTRADA de un portal (sesión caducada, acceso cerrado), DENTRO de su instalación: los clientes de los portales
// (services/apiPortal, apiReferidor, apiLaboratorio, apiAnestesista) lo preguntan antes de su vuelta de siempre. Por el puente, si la
// pantalla es de `zona` y no de una de `salvo`, va a `entrada` debajo de /l/<instalación> y contesta true; fuera del puente, false
// sin tocar nada (y la vuelta de siempre sigue igual).
export function entradaPorElPuente(zona, salvo, entrada, { win = typeof window !== 'undefined' ? window : null } = {}) {
  const p = _actual
  if (!p || !win || !win.location) return false
  const ruta = sinPrefijo(win.location.pathname, p)
  if (!ruta.startsWith(zona) || (salvo || []).some((s) => ruta.startsWith(s))) return true
  win.location.href = p.base + entrada
  return true
}

let _actual = null
export function puente() { return _actual }

export function instalar({ win = typeof window !== 'undefined' ? window : null } = {}) {
  if (!win || !win.location) return null
  const p = detectar(win.location.pathname)
  if (!p) return null
  _actual = p
  try { win.__BIANGO_PUENTE__ = p } catch (_) { /* sin ventana */ }
  const origen = win.location.origin || ''
  const resto = sinPrefijo(win.location.pathname, p)
  // Un enlace a una ruta del servidor (p. ej. un archivo compartido de la Nube): a su sitio en el puente, y nada más.
  if (resto.startsWith('/api/')) { win.location.replace(p.api + resto + (win.location.search || '')); return p }
  if (resto === '/' && win.history && win.history.replaceState) win.history.replaceState(null, '', p.base + '/portal' + (win.location.search || '') + (win.location.hash || ''))

  // LO GUARDADO EN EL NAVEGADOR, aparte por instalación.
  const pre = prefijoAlmacen(p)
  try {
    const S = win.Storage && win.Storage.prototype
    if (S && !S.__puenteLocal) {
      const { getItem, setItem, removeItem } = S
      S.getItem = function (k) { return getItem.call(this, pre + String(k)) }
      S.setItem = function (k, v) { return setItem.call(this, pre + String(k), v) }
      S.removeItem = function (k) { return removeItem.call(this, pre + String(k)) }
      S.__puenteLocal = true
    }
  } catch (_) { /* un navegador sin almacenamiento: nada que separar */ }

  // TODA petición a /api/... de la página, por el puente.
  try {
    const X = win.XMLHttpRequest && win.XMLHttpRequest.prototype
    if (X && !X.__puenteLocal) {
      const open = X.open
      X.open = function (metodo, url, ...resto2) { return open.call(this, metodo, rutaApi(typeof url === 'string' ? url : String(url), p, origen), ...resto2) }
      X.__puenteLocal = true
    }
  } catch (_) { /* sin XMLHttpRequest */ }
  try {
    if (typeof win.fetch === 'function' && !win.fetch.__puenteLocal) {
      const f = win.fetch
      const nuevo = function (input, init) {
        if (typeof input === 'string') return f.call(this, rutaApi(input, p, origen), init)
        if (input && typeof input.url === 'string' && win.Request && input instanceof win.Request) {
          const u = rutaApi(input.url, p, origen)
          if (u !== input.url) return f.call(this, new win.Request(u, input), init)
        }
        return f.call(this, input, init)
      }
      nuevo.__puenteLocal = true
      win.fetch = nuevo
    }
  } catch (_) { /* sin fetch */ }
  try {
    const n = win.navigator
    if (n && typeof n.sendBeacon === 'function' && !n.sendBeacon.__puenteLocal) {
      const b = n.sendBeacon.bind(n)
      const nuevo = (url, datos) => b(rutaApi(String(url), p, origen), datos)
      nuevo.__puenteLocal = true
      n.sendBeacon = nuevo
    }
  } catch (_) { /* sin sendBeacon */ }
  return p
}

// Solo para las pruebas.
export function _olvidar() { _actual = null }
